import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical, Trash2, Plus, FolderGit2 } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useAISettings } from '@/hooks/useAISettings';
import type { ProjectTemplate } from '@/contexts/AISettingsContext';

interface SortableTemplateItemProps {
  template: ProjectTemplate;
  onUpdate: (template: ProjectTemplate) => void;
  onRemove: (id: string) => void;
  showDragHandle: boolean;
}

function SortableTemplateItem({ template, onUpdate, onRemove, showDragHandle }: SortableTemplateItemProps) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: template.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <AccordionItem
      ref={setNodeRef}
      style={style}
      value={template.id}
      className="border rounded-xl bg-card"
    >
      <AccordionTrigger className="px-4 py-3 hover:no-underline">
        <div className="flex items-center gap-3 w-full mr-3">
          {showDragHandle && (
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-muted-foreground hover:text-foreground"
            >
              <GripVertical className="h-4 w-4" />
            </div>
          )}
          <FolderGit2 size={16} className="text-muted-foreground" />
          <span className="text-sm font-medium">
            {template.name}
          </span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor={`${template.id}-name`} className="text-sm">{t('templateName')}</Label>
            <Input
              id={`${template.id}-name`}
              placeholder="MKStack"
              value={template.name}
              onChange={(e) => onUpdate({ ...template, name: e.target.value })}
              className="h-9"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${template.id}-description`} className="text-sm">{t('description')}</Label>
            <Textarea
              id={`${template.id}-description`}
              placeholder={t('templateDescriptionPlaceholder')}
              value={template.description}
              onChange={(e) => onUpdate({ ...template, description: e.target.value })}
              className="min-h-[100px] text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${template.id}-url`} className="text-sm">{t('gitUrl')}</Label>
            <Input
              id={`${template.id}-url`}
              placeholder="https://gitlab.com/soapbox-pub/mkstack.git"
              value={template.url}
              onChange={(e) => onUpdate({ ...template, url: e.target.value })}
              className="h-9"
            />
          </div>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onRemove(template.id)}
            className="gap-2"
          >
            <Trash2 className="h-4 w-4" />
            {t('delete')}
          </Button>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

export function ProjectTemplates() {
  const { t } = useTranslation();
  const { settings, updateProjectTemplate, removeProjectTemplate, setProjectTemplates, addProjectTemplate } = useAISettings();
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [newTemplateUrl, setNewTemplateUrl] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = settings.projectTemplates.findIndex(t => t.id === active.id);
      const newIndex = settings.projectTemplates.findIndex(t => t.id === over?.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const newTemplates = arrayMove(settings.projectTemplates, oldIndex, newIndex);
        setProjectTemplates(newTemplates);
      }
    }
  };

  const handleAddTemplate = () => {
    if (!newTemplateName.trim() || !newTemplateUrl.trim()) return;

    const id = newTemplateName.trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    // Check if ID already exists
    if (settings.projectTemplates.some(t => t.id === id)) {
      return;
    }

    addProjectTemplate({
      id,
      name: newTemplateName.trim(),
      description: newTemplateDescription.trim(),
      url: newTemplateUrl.trim(),
    });

    setNewTemplateName('');
    setNewTemplateDescription('');
    setNewTemplateUrl('');
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <FolderGit2 className="h-4 w-4" />
          <h3 className="text-sm font-semibold">{t('projectTemplates')}</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          {t('projectTemplatesDescription')}
        </p>
      </div>

      {/* Existing Templates */}
      {settings.projectTemplates.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={settings.projectTemplates.map(t => t.id)}
            strategy={verticalListSortingStrategy}
          >
            <Accordion type="multiple" className="space-y-2">
              {settings.projectTemplates.map((template) => (
                <SortableTemplateItem
                  key={template.id}
                  template={template}
                  onUpdate={updateProjectTemplate}
                  onRemove={removeProjectTemplate}
                  showDragHandle={settings.projectTemplates.length > 1}
                />
              ))}
            </Accordion>
          </SortableContext>
        </DndContext>
      )}

      {/* Add New Template */}
      <Accordion type="single" collapsible>
        <AccordionItem value="add-template" className="border rounded-xl bg-card">
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <div className="flex items-center gap-3">
              <Plus className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">{t('addProjectTemplate')}</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-template-name" className="text-sm">{t('templateName')}</Label>
                <Input
                  id="new-template-name"
                  placeholder="MKStack"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-template-description" className="text-sm">{t('description')}</Label>
                <Textarea
                  id="new-template-description"
                  placeholder={t('templateDescriptionPlaceholder')}
                  value={newTemplateDescription}
                  onChange={(e) => setNewTemplateDescription(e.target.value)}
                  className="min-h-[100px] text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-template-url" className="text-sm">{t('gitUrl')}</Label>
                <Input
                  id="new-template-url"
                  placeholder="https://gitlab.com/soapbox-pub/mkstack.git"
                  value={newTemplateUrl}
                  onChange={(e) => setNewTemplateUrl(e.target.value)}
                  className="h-9"
                />
              </div>
              <Button
                onClick={handleAddTemplate}
                disabled={!newTemplateName.trim() || !newTemplateUrl.trim() || settings.projectTemplates.some(t => t.id === newTemplateName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''))}
                className="w-full gap-2"
              >
                <Plus className="h-4 w-4" />
                {t('add')}
              </Button>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

export default ProjectTemplates;
